import { isEmpty, isNil } from 'lodash';
import { useListWorkflowTemplates, useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { isSubformComponent } from '../utils';

export interface Subform {
  workflowTemplate: MBAPI.WorkflowTemplate;
  componentId: string;
  createdAt?: string;
}

interface UseSubformsHook {
  subforms?: Subform[];
  loading: boolean;
  error?: Error;
  hasMore?: boolean;
  loadMore?(): void;
}

export function useSubforms({
  workflowTemplateId,
  name,
  loadAll = false,
}: {
  workflowTemplateId?: string | null;
  name?: string;
  loadAll?: boolean;
}): UseSubformsHook {
  const {
    workflowTemplate,
    loading: workflowTemplateLoading,
    error: workflowTemplateError,
  } = useWorkflowTemplate(workflowTemplateId);

  const subformComponents = workflowTemplate?.components?.filter(isSubformComponent) ?? [];
  const ids = subformComponents.map(c => c.workflowTemplateIds).flat();

  const {
    workflowTemplates,
    loading: workflowTemplatesLoading,
    error: workflowTemplatesError,
    hasMore,
    loadMore,
  } = useListWorkflowTemplates({
    skip: isEmpty(ids),
    input: { ids, name: isEmpty(name) ? undefined : name },
    loadAll,
  });
  const componentAndWorkflowTemplates = !workflowTemplatesLoading
    ? workflowTemplate?.components?.filter(isSubformComponent).map(c => ({
        componentId: c.id,
        workflowTemplates: !isEmpty(workflowTemplates)
          ? c.workflowTemplateIds.map(id => workflowTemplates?.find(wft => wft.id === id))
          : [],
      }))
    : [];
  const error = workflowTemplateError || workflowTemplatesError;
  const loading = workflowTemplateLoading || workflowTemplatesLoading;
  const subforms: Subform[] = [];
  componentAndWorkflowTemplates?.forEach(item => {
    item.workflowTemplates?.forEach(wft => {
      if (isNil(wft)) {
        return;
      }
      subforms.push({
        workflowTemplate: wft as MBAPI.WorkflowTemplate,
        componentId: item.componentId,
        createdAt: wft?.createdAt,
      });
    });
  });

  return { subforms, error, loading, hasMore, loadMore };
}
